import { useT } from '@/i18n';

/** The sign-in hero: one large night-ocean composition around the original
 *  stacked ORCA artwork — deep water, a calm horizon of light, a thin market
 *  line drawn along it and a few signal points. Not a map of features: the
 *  form beside it is the main object. Pure CSS/SVG (no photos); every motion
 *  is slow and stops with «reduce motion». Hidden on phones (compact brand). */

// a calm, slightly rising market line along the horizon (600×420 stage)
const LINE = 'M0 262 C40 258 62 266 96 254 S150 236 184 244 S236 262 268 238 S318 206 352 214 S404 236 438 210 S494 178 528 186 S580 170 600 160';
const SIGNALS = [[184, 244], [352, 214], [528, 186]] as const;

export function AuthVisual() {
  const { t } = useT('auth');
  return (
    <aside className="auth-visual" aria-label={t('visual.label')}>
      <div className="av-frame">
        <div className="av-sky" aria-hidden="true" />
        <svg className="av-sea" viewBox="0 0 600 420" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
          <defs>
            <linearGradient id="avLine" x1="0" x2="1">
              <stop offset="0" stopColor="#cfe0ff" stopOpacity="0" />
              <stop offset=".35" stopColor="#cfe0ff" stopOpacity=".55" />
              <stop offset=".8" stopColor="#f3f7ff" stopOpacity=".95" />
              <stop offset="1" stopColor="#ffb78f" stopOpacity=".9" />
            </linearGradient>
            <linearGradient id="avWave" x1="0" x2="1">
              <stop offset="0" stopColor="#9fbcf0" stopOpacity="0" />
              <stop offset=".5" stopColor="#9fbcf0" stopOpacity=".38" />
              <stop offset="1" stopColor="#9fbcf0" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* the sea: thin swells in perspective, closer = lower and wider apart */}
          <g className="av-waves">
            {[300, 312, 326, 343, 364, 390].map((y, i) => (
              <path key={y} style={{ ['--w' as string]: i }} d={`M-40 ${y} Q 150 ${y - 5 - i} 300 ${y} T 640 ${y}`} />
            ))}
          </g>
          {/* the market line on the horizon, with three signal points */}
          <path className="av-line-glow" d={LINE} />
          <path className="av-line" d={LINE} pathLength={1} />
          {SIGNALS.map(([x, y], i) => (
            <g key={x} className="av-signal" style={{ ['--i' as string]: i }}>
              <circle cx={x} cy={y} r="9" />
              <circle cx={x} cy={y} r="2.6" />
            </g>
          ))}
        </svg>
        <div className="av-logo-wrap">
          <div className="av-halo" aria-hidden="true" />
          <img className="av-logo" src="/assets/brand/orca-logo.png" alt={t('visual.logoAlt')} width={445} height={464} decoding="async" />
        </div>
        <div className="av-copy">
          <span className="av-eyebrow">{t('visual.eyebrow')}</span>
          <p className="av-slogan">{t('visual.slogan')}</p>
          <p className="av-domains">{t('visual.domains')}</p>
        </div>
      </div>
    </aside>
  );
}
